import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Componente para generar QR codes como imágenes base64
 * Se usa fuera del PDF para convertir el SVG a imagen
 */
const QRCodeGenerator = ({ value, logo, size = 200, fgColor = '#000000', onGenerated }) => {
    const containerRef = useRef(null);
    const [logoBase64, setLogoBase64] = useState(null);
    const [isReady, setIsReady] = useState(false);

    // Convertir logo a base64
    useEffect(() => {
        if (!logo) {
            setIsReady(true);
            return;
        }

        // Si ya es base64, usarlo directamente
        if (logo.startsWith('data:')) {
            setLogoBase64(logo);
            setIsReady(true);
            return;
        }

        const convertLogoToBase64 = async () => {
            try {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                
                img.onload = () => {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.width;
                        canvas.height = img.height;
                        
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        
                        const base64 = canvas.toDataURL('image/png');
                        setLogoBase64(base64);
                        setIsReady(true);
                    } catch (err) {
                        console.error('Error en canvas:', err);
                        setIsReady(true);
                    }
                };

                img.onerror = (err) => {
                    console.error('Error cargando logo:', logo, err);
                    setIsReady(true);
                };

                img.src = logo;
            } catch (error) {
                console.error('Error convirtiendo logo a base64:', error);
                setIsReady(true);
            }
        };

        convertLogoToBase64();
    }, [logo]);

    // Generar QR una vez que esté listo
    useEffect(() => {
        if (!isReady || !containerRef.current) return;

        const generateQR = () => {
            const svg = containerRef.current?.querySelector('svg');
            if (!svg) {
                console.error('No se encontró el SVG del QR');
                return;
            }

            try {
                // Convertir SVG a imagen base64
                const svgData = new XMLSerializer().serializeToString(svg);
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                canvas.width = size;
                canvas.height = size;

                img.onload = () => {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, size, size);
                    ctx.drawImage(img, 0, 0);
                    const pngData = canvas.toDataURL('image/png');
                    onGenerated(pngData);
                };

                img.onerror = (err) => {
                    console.error('Error generando imagen del QR:', err);
                };

                const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
                const url = URL.createObjectURL(svgBlob);
                img.src = url;
            } catch (error) {
                console.error('Error en generateQR:', error);
            }
        };

        // Dar tiempo para que el QR se renderice
        const timeout = setTimeout(generateQR, 800);
        return () => clearTimeout(timeout);
    }, [isReady, value, logoBase64, size, onGenerated]);

    return (
        <div ref={containerRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
            <QRCodeSVG
                value={value}
                size={size}
                level="H"
                includeMargin={true}
                fgColor={fgColor}
                bgColor="#ffffff"
                imageSettings={logoBase64 ? {
                    src: logoBase64,
                    height: size * 0.25,
                    width: size * 0.25,
                    excavate: true,
                } : undefined}
            />
        </div>
    );
};

export default QRCodeGenerator;
